import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';
import { compressImage } from '../utils/compressImage';

const FALLBACK_IMG = '/images/contenuti/box1.webp';

type CardRow = {
  id_cliente: number;
  card_titolo: string | null;
  card_testo: string | null;
  card_foto: string | null;
  comune_id: string | null;
  id_categoria: number | null;
  tipologia: string | null;
  latitudine: string | null;
  longitudine: string | null;
  codice_riscatto: string | null;
  indirizzo_reale: string | null;
  store_id: number | null;
  account_attivo: boolean;
  comune_nome: string | null;
  nome_categoria: string | null;
};

type Categoria = { id_categoria: number; nome_categoria: string };
type Provincia = { id_provincia: number; nome: string; sigla: string };
type Comune = { comune_id: string; comune_nome: string; id_provincia: number };

type EditorState = {
  id_cliente: number | null;
  titolo: string;
  testo: string;
  id_provincia: string;
  comune_id: string;
  id_categoria: string;
  latitudine: string;
  longitudine: string;
  fotoFile: File | null;
};

function emptyEditor(): EditorState {
  return {
    id_cliente: null,
    titolo: '',
    testo: '',
    id_provincia: '',
    comune_id: '',
    id_categoria: '1',
    latitudine: '',
    longitudine: '',
    fotoFile: null,
  };
}

export function MasterPortaleCardsPage() {
  const [righe, setRighe] = useState<CardRow[]>([]);
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [province, setProvince] = useState<Provincia[]>([]);
  const [comuni, setComuni] = useState<Comune[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cardsRes, catRes, provRes, comRes] = await Promise.all([
        apiFetch('/api/v1/portale/master/cards'),
        fetch('/api/v1/portale/public/categorie'),
        fetch('/api/v1/portale/public/province'),
        fetch('/api/v1/portale/public/comuni'),
      ]);
      const cards = (await cardsRes.json().catch(() => [])) as CardRow[] | { error?: string };
      if (!cardsRes.ok) throw new Error((cards as { error?: string })?.error || `HTTP ${cardsRes.status}`);
      setRighe(Array.isArray(cards) ? cards : []);
      setCategorie(((await catRes.json().catch(() => [])) as Categoria[]) || []);
      setProvince(((await provRes.json().catch(() => [])) as Provincia[]) || []);
      setComuni(((await comRes.json().catch(() => [])) as Comune[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function apriEditor(row: CardRow | null) {
    setMessage(null);
    setError(null);
    if (!row) {
      setEditor(emptyEditor());
      return;
    }
    // Find the provincia for this comune
    const comune = comuni.find((c) => c.comune_id === row.comune_id);
    setEditor({
      id_cliente: row.id_cliente,
      titolo: row.card_titolo || '',
      testo: row.card_testo || '',
      id_provincia: comune ? String(comune.id_provincia) : '',
      comune_id: row.comune_id || '',
      id_categoria: String(row.id_categoria || '1'),
      latitudine: row.latitudine || '',
      longitudine: row.longitudine || '',
      fotoFile: null,
    });
  }

  async function salva(event: FormEvent) {
    event.preventDefault();
    if (!editor) return;
    setBusy(true);
    setError(null);
    try {
      let foto: string | null = null;
      if (editor.fotoFile) {
        // Compress client-side: 5MB photo → ~150KB WebP before upload
        const compressed = await compressImage(editor.fotoFile);
        const formData = new FormData();
        formData.append('image', compressed);
        const upRes = await apiFetch('/api/v1/portale/master/upload-foto', {
          method: 'POST',
          body: formData,
        });
        const upBody = (await upRes.json().catch(() => ({}))) as { foto?: string; error?: string };
        if (!upRes.ok) throw new Error(upBody?.error || `upload ${upRes.status}`);
        foto = upBody.foto || null;
      }

      const payload = {
        titolo: editor.titolo.trim(),
        testo: editor.testo.trim(),
        comune_id: editor.comune_id,
        id_categoria: Number(editor.id_categoria),
        latitudine: editor.latitudine.trim() || null,
        longitudine: editor.longitudine.trim() || null,
        foto,
      };

      const url = editor.id_cliente
        ? `/api/v1/portale/master/cards/${editor.id_cliente}`
        : '/api/v1/portale/master/cards';
      const res = await apiFetch(url, {
        method: editor.id_cliente ? 'PUT' : 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setMessage(editor.id_cliente ? 'Card aggiornata' : 'Card creata e pubblicata');
      setEditor(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function elimina(row: CardRow) {
    const nome = row.card_titolo || `#${row.id_cliente}`;
    if (!window.confirm(`Rimuovere definitivamente la card "${nome}" dal portale?`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/portale/master/cards/${row.id_cliente}`, {
        method: 'DELETE',
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setMessage(`Card "${nome}" eliminata`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Portale · CMS Card"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · gestione delle card esposte nella vetrina
            pubblica del portale.
          </>
        }
        actions={
          <button type="button" className="sk-btn" onClick={() => apriEditor(null)}>
            + Nuova card manuale
          </button>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      <SectionCard title={`${righe.length} card`}>
        <DataTable
          emptyMessage="Nessuna card pubblicata"
          rows={righe.map((r) => ({ ...r, row_key: String(r.id_cliente) }))}
          columns={[
            {
              key: 'card_foto',
              label: 'Foto',
              render: (_v, row) => (
                <img
                  src={String(row.card_foto || FALLBACK_IMG)}
                  alt=""
                  style={{
                    width: 56,
                    height: 42,
                    objectFit: 'cover',
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                  }}
                />
              ),
            },
            {
              key: 'card_titolo',
              label: 'Insegna',
              render: (_v, row) => (
                <span>
                  <strong>{String(row.card_titolo || 'Senza titolo')}</strong>
                  {row.store_id ? (
                    <span className="sk-muted"> · store #{row.store_id}</span>
                  ) : null}
                </span>
              ),
            },
            { key: 'comune_nome', label: 'Comune', render: (v) => String(v || '—') },
            { key: 'nome_categoria', label: 'Categoria', render: (v) => String(v || '—') },
            {
              key: 'codice_riscatto',
              label: 'Codice riscatto',
              render: (v) =>
                v ? (
                  <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{String(v)}</code>
                ) : (
                  '—'
                ),
            },
            {
              key: 'latitudine',
              label: 'GPS',
              render: (_v, row) =>
                row.latitudine && row.longitudine
                  ? `${Number(row.latitudine).toFixed(3)}, ${Number(row.longitudine).toFixed(3)}`
                  : 'No GPS',
            },
            {
              key: 'actions',
              label: '',
              render: (_v, row) => (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    type="button"
                    className="sk-btn sk-btn--sm"
                    disabled={busy}
                    onClick={() => apriEditor(row as unknown as CardRow)}
                  >
                    Modifica
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--quiet sk-btn--sm"
                    disabled={busy}
                    onClick={() => void elimina(row as unknown as CardRow)}
                  >
                    Elimina
                  </button>
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      {editor ? (
        <SectionCard
          title={editor.id_cliente ? `Modifica card #${editor.id_cliente}` : 'Nuova card manuale'}
        >
          <form onSubmit={salva} className="sk-saas-form">
            <label className="sk-field">
              <span>Titolo / Insegna</span>
              <input
                className="sk-input"
                value={editor.titolo}
                onChange={(e) => setEditor({ ...editor, titolo: e.target.value })}
                required
                disabled={busy}
              />
            </label>
            <label className="sk-field">
              <span>Descrizione vetrina</span>
              <textarea
                className="sk-input"
                rows={3}
                value={editor.testo}
                onChange={(e) => setEditor({ ...editor, testo: e.target.value })}
                disabled={busy}
              />
            </label>
            <label className="sk-field">
              <span>Provincia</span>
              <select
                className="sk-input"
                value={editor.id_provincia}
                onChange={(e) => setEditor({ ...editor, id_provincia: e.target.value, comune_id: '' })}
                required
                disabled={busy}
              >
                <option value="">Seleziona la provincia…</option>
                {province.map((p) => (
                  <option key={p.id_provincia} value={String(p.id_provincia)}>
                    {p.nome} ({p.sigla})
                  </option>
                ))}
              </select>
            </label>
            <label className="sk-field">
              <span>Comune</span>
              <select
                className="sk-input"
                value={editor.comune_id}
                onChange={(e) => setEditor({ ...editor, comune_id: e.target.value })}
                required
                disabled={busy || !editor.id_provincia}
              >
                <option value="">Seleziona il comune…</option>
                {comuni
                  .filter((c) => !editor.id_provincia || String(c.id_provincia) === editor.id_provincia)
                  .map((c) => (
                    <option key={c.comune_id} value={c.comune_id}>
                      {c.comune_nome}
                    </option>
                  ))}
              </select>
            </label>
            <label className="sk-field">
              <span>Categoria</span>
              <select
                className="sk-input"
                value={editor.id_categoria}
                onChange={(e) => setEditor({ ...editor, id_categoria: e.target.value })}
                required
                disabled={busy}
              >
                {categorie.map((c) => (
                  <option key={c.id_categoria} value={String(c.id_categoria)}>
                    {c.nome_categoria}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <label className="sk-field" style={{ flex: 1 }}>
                <span>Latitudine</span>
                <input
                  className="sk-input"
                  value={editor.latitudine}
                  onChange={(e) => setEditor({ ...editor, latitudine: e.target.value })}
                  disabled={busy}
                />
              </label>
              <label className="sk-field" style={{ flex: 1 }}>
                <span>Longitudine</span>
                <input
                  className="sk-input"
                  value={editor.longitudine}
                  onChange={(e) => setEditor({ ...editor, longitudine: e.target.value })}
                  disabled={busy}
                />
              </label>
            </div>
            <label className="sk-field">
              <span>Foto card (JPG, PNG o WebP)</span>
              <input
                className="sk-input"
                type="file"
                accept="image/*"
                onChange={(e) => setEditor({ ...editor, fotoFile: e.target.files?.[0] || null })}
                disabled={busy}
              />
            </label>
            <div className="sk-saas-actions">
              <button type="submit" className="sk-btn" disabled={busy}>
                {busy ? 'Salvataggio…' : 'Salva ed esponi'}
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--quiet"
                onClick={() => setEditor(null)}
                disabled={busy}
              >
                Annulla
              </button>
            </div>
          </form>
      </SectionCard>
      ) : null}
    </section>
  );
}
